/**
 * Simple carousel component initially inteded for usage in the secondary navigation and AI chatbot predefined questions.
 *
 * Requirements: add `.vm-carousel-container` class to an element that has a `.vm-carousel-track` block element that uses `display: flex`.
 *
 * [USAGE]

```scss
// Usage code inside a view style file where carousel should be displayed.
@use "Styles/components/carousel";

.vm-carousel-container {
    // Container that holds another element that acts as a carousel track.
}
```

```javascript
// Usage code inside a view JS file where carousel should be displayed.
import VuMediCarousel from 'Utils/vm-carousel';

const containerElement = document.querySelector('.vm-carousel-container');
const carousel = new VuMediCarousel(containerElement);
carousel.initialize();

// If not needed, e.g. in case of a mobile screen
carousel.deinitialize();
```
 */
const STEP_SIZE = 220;

export default class VuMediCarousel {
    constructor(containerElement) {
        this.containerElement = containerElement;
        this.trackElement = null;
        this.buttonPrev = null;
        this.buttonNext = null;
        this.onMoved = null;

        this.currentOffset = 0;
    }

    _handlePrevClick() {
        if (!this.trackElement) {
            return;
        }

        this.currentOffset += STEP_SIZE;

        if (this.currentOffset > 0) {
            this.currentOffset = 0;
        }

        this._handleMovement();
    }

    _handleNextClick() {
        if (!this.trackElement) {
            return;
        }

        this.currentOffset -= STEP_SIZE;

        const containerWidth = this.containerElement.offsetWidth;
        const trackWidth = this.trackElement.offsetWidth;

        if (this.currentOffset < containerWidth - trackWidth) {
            this.currentOffset = containerWidth - trackWidth;
        }

        this._handleMovement();
    }

    _handleMovement() {
        this.trackElement.style.transform = `translateX(${this.currentOffset.toString()}px)`;

        if (this.currentOffset === 0) {
            this.containerElement.setAttribute('data-activated', 'next');
        } else if (this.currentOffset === this.containerElement.offsetWidth - this.trackElement.offsetWidth) {
            this.containerElement.setAttribute('data-activated', 'prev');
        } else {
            this.containerElement.setAttribute('data-activated', '');
        }

        if (typeof this.onMoved === 'function') {
            this.onMoved();
        }
    }

    initialize() {
        if (!this.containerElement) {
            return;
        }

        this.trackElement = this.containerElement.querySelector('.vm-carousel-track');

        if (this.containerElement.offsetWidth >= this.trackElement.offsetWidth) {
            this.containerElement.setAttribute('data-deactivated', '');
            this.containerElement.removeAttribute('data-activated');
            return;
        }

        this.containerElement.setAttribute('data-activated', '');
        this.containerElement.removeAttribute('data-deactivated');

        if (this.buttonPrev && this.buttonNext) {
            return;
        }

        const buttonPrevBackdrop = document.createElement('div');
        const buttonNextBackdrop = document.createElement('div');

        buttonPrevBackdrop.classList.add('vm-carousel-button-backdrop', 'vm-carousel-button-backdrop--prev');
        buttonNextBackdrop.classList.add('vm-carousel-button-backdrop', 'vm-carousel-button-backdrop--next');

        this.buttonPrev = document.createElement('button');
        this.buttonNext = document.createElement('button');

        this.buttonPrev.classList.add('vm-carousel-button', 'vm-carousel-button--prev');
        this.buttonNext.classList.add('vm-carousel-button', 'vm-carousel-button--next');

        this.buttonPrev.addEventListener('click', () => this._handlePrevClick());
        this.buttonNext.addEventListener('click', () => this._handleNextClick());

        const iconPrev = document.createElement('i');
        const iconNext = document.createElement('i');

        iconPrev.classList.add('icon', 'icon-chevron-left');
        iconNext.classList.add('icon', 'icon-chevron-right');

        this.buttonPrev.appendChild(iconPrev);
        this.buttonNext.appendChild(iconNext);

        buttonPrevBackdrop.appendChild(this.buttonPrev);
        buttonNextBackdrop.appendChild(this.buttonNext);

        this.containerElement.appendChild(buttonPrevBackdrop);
        this.containerElement.appendChild(buttonNextBackdrop);

        window.setTimeout(() => {
            this._handleMovement();
        }, 1);
    }

    deinitialize() {
        this.containerElement.removeAttribute('data-activated');
        this.containerElement.removeAttribute('data-deactivated');
        this.currentOffset = 0;
    }
}
